import React, { useRef } from 'react';
import { TouchableOpacity, StyleSheet, Animated, Text, Platform, ActivityIndicator } from 'react-native';
import GradientText from './GradientText';
import { LinearGradient } from 'expo-linear-gradient';

const GradientButton = ({ 
    onPress, 
    text, 
    style, 
    textStyle, 
    disabled = false,
    loading = false,
    gradientColors = ["#707070", "#EFEFEF"],
    fontSize = 15,
    fontFamily = 'Slate',
    fontWeight = '500',
    ...props 
}) => {
    const glowAnimation = useRef(new Animated.Value(0)).current;
    const scaleAnimation = useRef(new Animated.Value(1)).current;

    const buttonStyle = [
        styles.button,
        style,
        (disabled || loading) && styles.disabledButton
    ];

    const textStyleCombined = [
        styles.buttonText,
        textStyle,
        disabled && styles.disabledText
    ];

    const handleHoverIn = () => {
        if (disabled) return;
        
        Animated.timing(glowAnimation, {
            toValue: 1,
            duration: 200,
            useNativeDriver: false,
        }).start();
    };

    const handleHoverOut = () => {
        if (disabled) return;
        
        Animated.timing(glowAnimation, {
            toValue: 0,
            duration: 200,
            useNativeDriver: false,
        }).start();
    };

    const handlePressIn = () => {
        if (disabled) return;
        
        Animated.spring(scaleAnimation, {
            toValue: 0.98,
            useNativeDriver: true,
        }).start();
    };

    const handlePressOut = () => {
        if (disabled) return;
        
        Animated.spring(scaleAnimation, {
            toValue: 1,
            useNativeDriver: true,
        }).start();
    };

    return (
        <Animated.View style={{ transform: [{ scale: scaleAnimation }] }}>
            <TouchableOpacity 
                style={buttonStyle} 
                onPress={onPress} 
                onPressIn={handlePressIn}
                onPressOut={handlePressOut}
                onMouseEnter={handleHoverIn}
                onMouseLeave={handleHoverOut}
                disabled={disabled || loading}
                activeOpacity={1}
                {...props}
            >
                <LinearGradient
                    style={styles.gradientBackground}
                    colors={['rgba(255, 255, 255, 0.02)', 'rgba(255, 255, 255, 0.06)']}
                />

                {loading ? (
                    <ActivityIndicator size="small" color="rgba(255,255,255,0.8)" />
                ) : (
                    <>
                        {/* Glow Shadow */}
                        <Animated.View 
                            style={[
                                styles.glowContainer,
                                {
                                    opacity: glowAnimation,
                                    shadowOpacity: glowAnimation.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0, 0.8]
                                    }),
                                    shadowRadius: glowAnimation.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0, 8]
                                    }),
                                }
                            ]}
                        >
                            <Text
                                style={[
                                    textStyleCombined,
                                    styles.glowText
                                ]}
                            >
                                {text}
                            </Text>
                        </Animated.View>

                        {/* Main Text */}
                        <GradientText
                            gradientColors={gradientColors}
                            fontSize={fontSize}
                            fontFamily={fontFamily}
                            fontWeight={fontWeight}
                            style={textStyleCombined}
                        >
                            {text}
                        </GradientText>
                    </>
                )}
            </TouchableOpacity>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    button: {
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 8,
        borderWidth: 1,
        borderTopColor: 'rgba(255, 255, 255, 0.20)',
        borderLeftColor: 'rgba(255, 255, 255, 0.18)',
        borderRightColor: 'rgba(255, 255, 255, 0.12)',
        borderBottomColor: 'rgba(255, 255, 255, 0.08)',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
    },
    gradientBackground: {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    glowContainer: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        justifyContent: 'center',
        alignItems: 'center',
        shadowColor: '#777777',
        shadowOffset: {
            width: 0,
            height: 0,
        },
        elevation: 8,
    },
    glowText: {
        color: 'white',
        ...(Platform.OS === 'web' ? {
            textShadow: '0 0 8px #777777',
        } : {
            textShadowColor: '#777777',
            textShadowOffset: { width: 0, height: 0 },
            textShadowRadius: 8,
        }),
    },
    buttonText: {
        fontSize: 15,
        fontFamily: 'Slate',
        fontWeight: '500',
        lineHeight: 20,
    },
    disabledButton: {
        opacity: 0.3,
    },
    disabledText: {
        color: 'rgba(255, 255, 255, 0.5)',
    },
});

export default GradientButton;